import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { validateTenantStatus } from '@/services/tenantInit'
import logger from '@/utils/logger'

// Import pages - Public
const HomePage = () => import('@/pages/public/HomePage.vue')
const StorePage = () => import('@/pages/public/StorePage.vue')
const ProductsPage = () => import('@/pages/public/ProductsPage.vue')
const ProductDetailPage = () => import('@/pages/public/ProductDetailPage.vue')
const CartPage = () => import('@/pages/public/CartPage.vue')
const CheckoutPage = () => import('@/pages/public/CheckoutPage.vue')
const CakeBookingPage = () => import('@/pages/public/CakeBookingPage.vue')

// Import pages - Auth
const LoginPage = () => import('@/pages/auth/Login.vue')
const RegisterPage = () => import('@/pages/auth/Register.vue')

// Import pages - Client
const ClientDashboard = () => import('@/pages/client/Dashboard.vue')
const ClientOrders = () => import('@/pages/client/Orders.vue')
const ClientProfile = () => import('@/pages/client/Profile.vue')
const ClientCalendar = () => import('@/pages/client/Calendar.vue')

// Import pages - Admin
const AdminDashboard = () => import('@/pages/admin/Dashboard.vue')
const AdminProducts = () => import('@/pages/admin/Products.vue')
const AdminProductEdit = () => import('@/pages/admin/ProductEdit.vue')
const AdminOrders = () => import('@/pages/admin/Orders.vue')
const AdminOrderDetail = () => import('@/pages/admin/OrderDetail.vue')
const AdminBookings = () => import('@/pages/admin/Bookings.vue')
const AdminClients = () => import('@/pages/admin/Clients.vue')
const AdminClientDetail = () => import('@/pages/admin/ClientDetail.vue')
const AdminRecipes = () => import('@/pages/admin/Recipes.vue')
const AdminProduction = () => import('@/pages/admin/Production.vue')
const AdminInventory = () => import('@/pages/admin/Inventory.vue')
const AdminCategories = () => import('@/pages/admin/Categories.vue')
const AdminCakeCustomizations = () => import('@/pages/admin/CakeCustomizations.vue')
const AdminPaymentSettings = () => import('@/pages/admin/PaymentSettings.vue')
const AdminReports = () => import('@/pages/admin/Reports.vue')
const AdminSettings = () => import('@/pages/admin/Settings.vue')

// Import pages - POS
const POSDashboard = () => import('@/pages/pos/Dashboard.vue')
const POSNewOrder = () => import('@/pages/pos/NewOrder.vue')
const POSTodayOrders = () => import('@/pages/pos/TodayOrders.vue')
const POSQuickReorder = () => import('@/pages/pos/QuickReorder.vue')

// Import pages - SuperAdmin
const SuperAdminDashboard = () => import('@/pages/superadmin/Dashboard.vue')
const SuperAdminTenantsList = () => import('@/pages/superadmin/TenantsList.vue')
const SuperAdminTenantCreate = () => import('@/pages/superadmin/TenantCreate.vue')
const SuperAdminTenantEdit = () => import('@/pages/superadmin/TenantEdit.vue')
const SuperAdminTenantDetails = () => import('@/pages/superadmin/TenantDetails.vue')
const SuperAdminTenantUsers = () => import('@/pages/superadmin/TenantUsers.vue')
const SuperAdminBilling = () => import('@/pages/superadmin/BillingManagement.vue')
const SuperAdminAnalytics = () => import('@/pages/superadmin/Analytics.vue')
const SuperAdminSettings = () => import('@/pages/superadmin/SystemSettings.vue')

// Import pages - Error pages
const TenantNotFound = () => import('@/pages/errors/TenantNotFound.vue')
const TenantSuspended = () => import('@/pages/errors/TenantSuspended.vue')
const SubscriptionExpired = () => import('@/pages/errors/SubscriptionExpired.vue')

const routes = [
    // Public routes
    {
        path: '/',
        name: 'home',
        component: HomePage,
        meta: { title: 'Home', layout: 'default' }
    },
    {
        path: '/store',
        name: 'store',
        component: StorePage,
        meta: { title: 'Negozio', layout: 'default' }
    },
    {
        path: '/store/:slug',
        name: 'store-category',
        component: StorePage,
        meta: { title: 'Categoria', layout: 'default' }
    },
    {
        path: '/products',
        name: 'products',
        component: ProductsPage,
        meta: { title: 'Prodotti', layout: 'default' }
    },
    {
        path: '/product/:id',
        name: 'product-detail',
        component: ProductDetailPage,
        meta: { title: 'Dettaglio Prodotto', layout: 'default' }
    },
    {
        path: '/cart',
        name: 'cart',
        component: CartPage,
        meta: { title: 'Carrello', layout: 'default' }
    },
    {
        path: '/checkout',
        name: 'checkout',
        component: CheckoutPage,
        meta: { title: 'Checkout', requiresAuth: true, layout: 'default' }
    },
    {
        path: '/cake-booking',
        name: 'cake-booking',
        component: CakeBookingPage,
        meta: { title: 'Prenota una Torta', layout: 'default' }
    },

    // Auth routes
    {
        path: '/auth/login',
        name: 'login',
        component: LoginPage,
        meta: { title: 'Accedi', layout: 'auth', guest: true }
    },
    {
        path: '/auth/register',
        name: 'register',
        component: RegisterPage,
        meta: { title: 'Registrati', layout: 'auth', guest: true }
    },
    {
        path: '/login',
        redirect: '/auth/login'
    },
    {
        path: '/register',
        redirect: '/auth/register'
    },

    // Client routes
    {
        path: '/client',
        redirect: '/client/dashboard'
    },
    {
        path: '/client/dashboard',
        name: 'client-dashboard',
        component: ClientDashboard,
        meta: { title: 'Dashboard Cliente', requiresAuth: true, roles: ['client'], layout: 'default' }
    },
    {
        path: '/client/orders',
        name: 'client-orders',
        component: ClientOrders,
        meta: { title: 'I Miei Ordini', requiresAuth: true, roles: ['client'], layout: 'default' }
    },
    {
        path: '/client/profile',
        name: 'client-profile',
        component: ClientProfile,
        meta: { title: 'Profilo', requiresAuth: true, roles: ['client'], layout: 'default' }
    },
    {
        path: '/client/calendar',
        name: 'client-calendar',
        component: ClientCalendar,
        meta: { title: 'Calendario Ricorrenze', requiresAuth: true, roles: ['client'], layout: 'default' }
    },

    // Admin routes - flat structure, no nested layouts
    {
        path: '/admin',
        redirect: '/admin/dashboard'
    },
    {
        path: '/admin/dashboard',
        name: 'admin-dashboard',
        component: AdminDashboard,
        meta: { title: 'Dashboard Admin', layout: 'admin', requiresAuth: true, roles: ['admin', 'manager'] }
    },
    {
        path: '/admin/products',
        name: 'admin-products',
        component: AdminProducts,
        meta: { title: 'Gestione Prodotti', layout: 'admin', requiresAuth: true, roles: ['admin', 'manager'] }
    },
    {
        path: '/admin/products/new',
        name: 'admin-product-new',
        component: AdminProductEdit,
        meta: { title: 'Nuovo Prodotto', layout: 'admin', requiresAuth: true, roles: ['admin', 'manager'] }
    },
    {
        path: '/admin/products/:id/edit',
        name: 'admin-product-edit',
        component: AdminProductEdit,
        meta: { title: 'Modifica Prodotto', layout: 'admin', requiresAuth: true, roles: ['admin', 'manager'] }
    },
    {
        path: '/admin/orders',
        name: 'admin-orders',
        component: AdminOrders,
        meta: { title: 'Gestione Ordini', layout: 'admin', requiresAuth: true, roles: ['admin', 'manager'] }
    },
    {
        path: '/admin/orders/:id',
        name: 'admin-order-detail',
        component: AdminOrderDetail,
        meta: { title: 'Dettaglio Ordine', layout: 'admin', requiresAuth: true, roles: ['admin', 'manager'] }
    },
    {
        path: '/admin/bookings',
        name: 'admin-bookings',
        component: AdminBookings,
        meta: { title: 'Gestione Prenotazioni', layout: 'admin', requiresAuth: true, roles: ['admin', 'manager'] }
    },
    {
        path: '/admin/clients',
        name: 'admin-clients',
        component: AdminClients,
        meta: { title: 'Gestione Clienti', layout: 'admin', requiresAuth: true, roles: ['admin', 'manager'] }
    },
    {
        path: '/admin/clients/:id',
        name: 'admin-client-detail',
        component: AdminClientDetail,
        meta: { title: 'Dettaglio Cliente', layout: 'admin', requiresAuth: true, roles: ['admin', 'manager'] }
    },
    {
        path: '/admin/recipes',
        name: 'admin-recipes',
        component: AdminRecipes,
        meta: { title: 'Ricette e Impasti', layout: 'admin', requiresAuth: true, roles: ['admin', 'manager'] }
    },
    {
        path: '/admin/production',
        name: 'admin-production',
        component: AdminProduction,
        meta: { title: 'Pianificazione Produzione', layout: 'admin', requiresAuth: true, roles: ['admin', 'manager'] }
    },
    {
        path: '/admin/inventory',
        name: 'admin-inventory',
        component: AdminInventory,
        meta: { title: 'Inventario e Acquisti', layout: 'admin', requiresAuth: true, roles: ['admin', 'manager'] }
    },
    {
        path: '/admin/categories',
        name: 'admin-categories',
        component: AdminCategories,
        meta: { title: 'Gestione Categorie', layout: 'admin', requiresAuth: true, roles: ['admin', 'manager'] }
    },
    {
        path: '/admin/cake-customizations',
        name: 'admin-cake-customizations',
        component: AdminCakeCustomizations,
        meta: { title: 'Personalizzazioni Torte', layout: 'admin', requiresAuth: true, roles: ['admin', 'manager'] }
    },
    {
        path: '/admin/payment-settings',
        name: 'admin-payment-settings',
        component: AdminPaymentSettings,
        meta: { title: 'Gestione Pagamenti', layout: 'admin', requiresAuth: true, roles: ['admin', 'manager'] }
    },
    {
        path: '/admin/reports',
        name: 'admin-reports',
        component: AdminReports,
        meta: { title: 'Report e Statistiche', layout: 'admin', requiresAuth: true, roles: ['admin', 'manager'] }
    },
    {
        path: '/admin/theme',
        name: 'admin-theme',
        component: () => import('@/pages/admin/ThemeCustomization.vue'),
        meta: { title: 'Personalizza Tema', layout: 'admin', requiresAuth: true, roles: ['admin', 'manager'] }
    },
    {
        path: '/admin/order-flow',
        name: 'admin-order-flow',
        component: () => import('@/pages/admin/OrderFlowConfig.vue'),
        meta: { title: 'Flusso Ordini', layout: 'admin', requiresAuth: true, roles: ['admin', 'manager'] }
    },
    {
        path: '/admin/settings',
        name: 'admin-settings',
        component: AdminSettings,
        meta: { title: 'Impostazioni', layout: 'admin', requiresAuth: true, roles: ['admin', 'manager'] }
    },

    // POS routes - flat structure, no nested layouts
    {
        path: '/pos',
        redirect: '/pos/dashboard'
    },
    {
        path: '/pos/dashboard',
        name: 'pos-dashboard',
        component: POSDashboard,
        meta: { title: 'POS Dashboard', layout: 'pos', requiresAuth: true, roles: ['pos', 'admin', 'manager'] }
    },
    {
        path: '/pos/new-order',
        name: 'pos-new-order',
        component: POSNewOrder,
        meta: { title: 'Nuovo Ordine', layout: 'pos', requiresAuth: true, roles: ['pos', 'admin', 'manager'] }
    },
    {
        path: '/pos/today',
        name: 'pos-today-orders',
        component: POSTodayOrders,
        meta: { title: 'Ordini di Oggi', layout: 'pos', requiresAuth: true, roles: ['pos', 'admin', 'manager'] }
    },
    {
        path: '/pos/reorder',
        name: 'pos-quick-reorder',
        component: POSQuickReorder,
        meta: { title: 'Riordino Veloce', layout: 'pos', requiresAuth: true, roles: ['pos', 'admin', 'manager'] }
    },

    // SuperAdmin routes - flat structure, no nested layouts
    {
        path: '/superadmin',
        redirect: '/superadmin/dashboard'
    },
    {
        path: '/superadmin/dashboard',
        name: 'superadmin-dashboard',
        component: SuperAdminDashboard,
        meta: { title: 'SuperAdmin Dashboard', subtitle: 'System Overview', layout: 'superadmin', requiresAuth: true, roles: ['super_admin'] }
    },
    {
        path: '/superadmin/tenants',
        name: 'superadmin-tenants',
        component: SuperAdminTenantsList,
        meta: { title: 'All Tenants', subtitle: 'Manage tenant accounts', layout: 'superadmin', requiresAuth: true, roles: ['super_admin'] }
    },
    {
        path: '/superadmin/tenants/new',
        name: 'superadmin-tenant-create',
        component: SuperAdminTenantCreate,
        meta: { title: 'Create Tenant', subtitle: 'Add new tenant account', layout: 'superadmin', requiresAuth: true, roles: ['super_admin'] }
    },
    {
        path: '/superadmin/tenants/:id',
        name: 'superadmin-tenant-details',
        component: SuperAdminTenantDetails,
        meta: { title: 'Tenant Details', subtitle: 'View tenant information', layout: 'superadmin', requiresAuth: true, roles: ['super_admin'] }
    },
    {
        path: '/superadmin/tenants/:id/edit',
        name: 'superadmin-tenant-edit',
        component: SuperAdminTenantEdit,
        meta: { title: 'Edit Tenant', subtitle: 'Update tenant settings', layout: 'superadmin', requiresAuth: true, roles: ['super_admin'] }
    },
    {
        path: '/superadmin/tenants/:id/users',
        name: 'superadmin-tenant-users',
        component: SuperAdminTenantUsers,
        meta: { title: 'Tenant Users', subtitle: 'Manage tenant users', layout: 'superadmin', requiresAuth: true, roles: ['super_admin'] }
    },
    {
        path: '/superadmin/billing',
        name: 'superadmin-billing',
        component: SuperAdminBilling,
        meta: { title: 'Billing Management', subtitle: 'Subscriptions and invoices', layout: 'superadmin', requiresAuth: true, roles: ['super_admin'] }
    },
    {
        path: '/superadmin/analytics',
        name: 'superadmin-analytics',
        component: SuperAdminAnalytics,
        meta: { title: 'System Analytics', subtitle: 'Performance metrics', layout: 'superadmin', requiresAuth: true, roles: ['super_admin'] }
    },
    {
        path: '/superadmin/settings',
        name: 'superadmin-settings',
        component: SuperAdminSettings,
        meta: { title: 'System Settings', subtitle: 'Global configuration', layout: 'superadmin', requiresAuth: true, roles: ['super_admin'] }
    },

    // Error routes
    {
        path: '/errors/tenant-not-found',
        name: 'tenant-not-found',
        component: TenantNotFound,
        meta: { title: 'Tenant Not Found', layout: 'empty', skipTenantValidation: true }
    },
    {
        path: '/errors/tenant-suspended',
        name: 'tenant-suspended',
        component: TenantSuspended,
        meta: { title: 'Account Suspended', layout: 'empty', skipTenantValidation: true }
    },
    {
        path: '/errors/subscription-expired',
        name: 'subscription-expired',
        component: SubscriptionExpired,
        meta: { title: 'Subscription Expired', layout: 'empty', skipTenantValidation: true }
    },

    // 404 route
    {
        path: '/:pathMatch(.*)*',
        name: 'not-found',
        component: () => import('@/pages/NotFound.vue'),
        meta: { title: 'Pagina non trovata', layout: 'default' }
    }
]

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes,
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition) {
            return savedPosition
        } else if (to.hash) {
            return {
                el: to.hash,
                behavior: 'smooth'
            }
        } else {
            return { top: 0 }
        }
    }
})

// Navigation guards con logging completo e tenant validation
router.beforeEach(async (to, from, next) => {
    logger.info('ROUTER', `🗺️ Navigation: ${from.fullPath} → ${to.fullPath}`, {
        fromRoute: from.name,
        toRoute: to.name,
        params: to.params,
        query: to.query,
        meta: to.meta
    })

    const authStore = useAuthStore()

    // Set page title
    document.title = to.meta.title
        ? `${to.meta.title} - Gestionale Pasticceria`
        : 'Gestionale Pasticceria'

    logger.debug('ROUTER', `📄 Page title set to: ${document.title}`)

    // Validate tenant status (skip for error pages and public routes)
    if (!to.meta.skipTenantValidation) {
        const tenantValidation = await validateTenantStatus()

        if (!tenantValidation.valid) {
            logger.warn('ROUTER', '❌ Tenant validation failed', {
                error: tenantValidation.error,
                message: tenantValidation.message
            })

            // Redirect to appropriate error page
            switch (tenantValidation.error) {
                case 'TENANT_NOT_FOUND':
                    return next({ name: 'tenant-not-found' })
                case 'TENANT_INACTIVE':
                case 'TENANT_SUSPENDED':
                    return next({ name: 'tenant-suspended' })
                case 'SUBSCRIPTION_EXPIRED':
                    return next({ name: 'subscription-expired' })
                default:
                    logger.warn('ROUTER', '⚠️ Unknown tenant error, continuing anyway')
                    // Continue anyway for unknown errors
            }
        }
    }

    // Check if route requires authentication
    if (to.meta.requiresAuth) {
        logger.debug('ROUTER', '🔐 Route requires authentication', {
            isAuthenticated: authStore.isAuthenticated,
            userRole: authStore.user?.role
        })

        if (!authStore.isAuthenticated) {
            logger.warn('ROUTER', '❌ Access denied: User not authenticated', {
                attemptedRoute: to.fullPath
            })
            // Mostra alert e reindirizza alla home
            alert('Devi effettuare il login per accedere a questa sezione. Vai alla homepage per accedere.')
            return next({ name: 'home' })
        }

        // Check role-based access
        if (to.meta.roles && to.meta.roles.length > 0) {
            const userRole = authStore.user?.role
            const hasAccess = to.meta.roles.includes(userRole)

            if (!hasAccess) {
                logger.warn('ROUTER', '❌ Access denied: Insufficient permissions', {
                    userRole: userRole,
                    requiredRoles: to.meta.roles,
                    attemptedRoute: to.fullPath
                })
                alert('Non hai i permessi per accedere a questa sezione.')
                return next({ name: 'home' })
            }
        }

        // Check admin access (legacy support)
        if (to.meta.requiresAdmin && !authStore.isAdmin) {
            logger.warn('ROUTER', '❌ Access denied: Admin role required', {
                userRole: authStore.user?.role,
                attemptedRoute: to.fullPath
            })
            return next({ name: 'home' })
        }
    }

    // Redirect authenticated users away from auth pages
    if (to.meta.guest && authStore.isAuthenticated) {
        logger.info('ROUTER', '🔄 Redirecting authenticated user from guest page', {
            userRole: authStore.user?.role,
            attemptedGuestRoute: to.fullPath
        })

        const userRole = authStore.user?.role
        if (userRole === 'super_admin') {
            return next({ name: 'superadmin-dashboard' })
        } else if (authStore.isAdmin) {
            return next({ name: 'admin-dashboard' })
        } else {
            return next({ name: 'client-dashboard' })
        }
    }

    logger.debug('ROUTER', '✅ Navigation approved')
    next()
})

router.afterEach((to, from) => {
    logger.info('ROUTER', `✅ Navigation completed to ${to.fullPath}`, {
        route: to.name,
        title: to.meta.title,
        layout: to.meta.layout
    })

    // Track page view (analytics)
    if (window.gtag) {
        window.gtag('config', 'GA_MEASUREMENT_ID', {
            page_path: to.fullPath
        })
        logger.debug('ROUTER', '📊 Google Analytics page view tracked')
    }
})

// Log navigation errors
router.onError((error) => {
    logger.error('ROUTER', `🚫 Router error: ${error.message}`, {
        error: error.stack,
        route: error.route?.fullPath
    })
})

export default router
